import NextHead from 'next/head';

import { useGlobalConfig } from '../../hooks/configContext';

import { getHeadBaseUrl } from './headContext';
import { HeadProps } from './types';
import { useCanonicalUrl } from './useCanonicalUrl';

function getImageUrl(baseUrl: string, imageUri: string) {
  const absoluteUrlRegexp = /^http/;
  if (!imageUri) {
    return null;
  }
  if (absoluteUrlRegexp.test(imageUri)) {
    return imageUri;
  }

  return `${baseUrl}${imageUri}`;
}

const getTruncatedDesciption = (description: string) => {
  return description.length > 300
    ? `${description.slice(0, 300)}...`
    : description;
};

const Head = ({
  children,
  description,
  image = '',
  imageHeight,
  imageWidth,
  noindex,
  canonicalURLOverride,
  preload,
  title,
  ...rest
}: HeadProps) => {
  const config = useGlobalConfig();

  let canonicalURL = useCanonicalUrl();
  if (canonicalURLOverride) {
    canonicalURL = canonicalURLOverride;
  }

  const baseUrl = getHeadBaseUrl();
  const imageUrl = getImageUrl(baseUrl, image);

  // Truncate the description to avoid too long texts being set in head
  const truncatedDesc = description ? getTruncatedDesciption(description) : '';

  return (
    <NextHead {...rest}>
      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={config.BU_CONFIG.locale_territory} />
      <meta property="og:site_name" content="Otovo" />
      {config.FACEBOOK_APP_ID && (
        <meta property="fb:app_id" content={config.FACEBOOK_APP_ID} />
      )}
      <meta name="twitter:site" content="@otovosolar" />
      {title && [
        <title key="title">{title}</title>,
        <meta key="og:title" property="og:title" content={title} />,
        <meta key="twitter:title" property="twitter:title" content={title} />,
      ]}
      {description && (
        <meta key="description" name="description" content={truncatedDesc} />
      )}
      {description && (
        <meta
          key="og:description"
          property="og:description"
          content={truncatedDesc}
        />
      )}
      {imageUrl && (
        <meta key={imageUrl} property="og:image" content={imageUrl} />
      )}
      {imageHeight && (
        <meta property="og:image:width" content={String(imageHeight)} />
      )}
      {imageWidth && (
        <meta property="og:image:height" content={String(imageWidth)} />
      )}
      {!!canonicalURL && (
        <>
          <meta property="og:url" content={canonicalURL} />
          <meta property="twitter:url" content={canonicalURL} />
          <link rel="canonical" href={canonicalURL} />
        </>
      )}
      {!!preload && <link rel="preload" href={preload} as="image" />}
      {!!noindex && <meta name="robots" content="noindex" />}
      <link
        href="https://fonts.googleapis.com/css?family=Poppins:400,500,600&display=swap"
        rel="stylesheet"
      />
      {children}
    </NextHead>
  );
};

export default Head;
